import {
  notificationPageSchema,
  notificationPolicySettingsSchema,
  notificationSettingsSchema,
  unreadCountSchema,
  type MarkNotificationsReadRequest,
  type UpdateNotificationPolicyRequest,
  type UpdateNotificationSettingsRequest,
} from '@mfos/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const NOTIFICATION_PAGE_SIZE = 30;
/** Fallback when the realtime stream is unavailable (e.g. behind a proxy that buffers SSE). */
const UNREAD_POLL_MS = 120_000;

export const notificationsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'notifications'] as const;
export const notificationSettingsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'settings', 'my-notifications'] as const;
export const notificationPolicyKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'settings', 'notifications'] as const;

export function useNotificationSettings(workspaceId: string) {
  return useQuery({
    queryKey: notificationSettingsKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'me', 'notification-settings'), {
        schema: notificationSettingsSchema,
        signal,
      }),
  });
}

export function useUpdateNotificationSettings(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateNotificationSettingsRequest) =>
    apiPut(
      workspacePath(workspaceId, 'me', 'notification-settings'),
      body,
      notificationSettingsSchema,
    ),
  );
}

export function useNotificationPolicy(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: notificationPolicyKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'settings', 'notifications'), {
        schema: notificationPolicySettingsSchema,
        signal,
      }),
    enabled,
  });
}

export function useUpdateNotificationPolicy(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateNotificationPolicyRequest) =>
    apiPut(
      workspacePath(workspaceId, 'settings', 'notifications'),
      body,
      notificationPolicySettingsSchema,
    ),
  );
}

export function useUnreadCount(workspaceId: string) {
  return useQuery({
    queryKey: [...notificationsKey(workspaceId), 'unread-count'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'notifications', 'unread-count'), {
        schema: unreadCountSchema,
        signal,
      }),
    refetchInterval: UNREAD_POLL_MS,
    retry: false,
  });
}

export function useNotifications(workspaceId: string, unreadOnly: boolean) {
  return useInfiniteQuery({
    queryKey: [...notificationsKey(workspaceId), 'list', unreadOnly] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'notifications'), {
          cursor: pageParam,
          limit: NOTIFICATION_PAGE_SIZE,
          unreadOnly: unreadOnly ? 'true' : 'false',
        }),
        { schema: notificationPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useMarkNotificationsRead(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: MarkNotificationsReadRequest) =>
    apiPost(workspacePath(workspaceId, 'notifications', 'read'), body, unreadCountSchema),
  );
}

/**
 * Notification links are in-app paths relative to the workspace (`/topics/<id>`). Anything that is
 * not a plain same-origin path is ignored so a stored link can never navigate elsewhere.
 */
export function notificationHref(workspaceId: string, link: string | null): string | null {
  if (link === null || !link.startsWith('/') || link.startsWith('//') || link.includes('\\')) {
    return null;
  }
  const base = `/w/${workspaceId}`;
  return link.startsWith(`${base}/`) || link === base ? link : `${base}${link}`;
}

export function unreadLabel(count: number | undefined): string {
  if (count === undefined) {
    return 'Notifications';
  }
  return count === 0 ? 'Notifications, none unread' : `Notifications, ${String(count)} unread`;
}
